// ============================================================
// Home.js
// ============================================================
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { getFeed, getFriends, addReaction, sendNudge } from '../lib/supabase'
import { formatDistanceToNow } from 'date-fns'
import { el } from 'date-fns/locale'

function Initials({ name }) {
  const parts = (name || '?').split(' ')
  const ini = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)
  return ini.toUpperCase()
}

function FeedCard({ session, currentUserId, onNudgeSent }) {
  const p = session.profiles
  const reactions = session.reactions || []
  const myFire  = reactions.some(r => r.user_id === currentUserId && r.type === 'fire')
  const myBravo = reactions.some(r => r.user_id === currentUserId && r.type === 'bravo')
  const fireCount  = reactions.filter(r => r.type === 'fire').length
  const bravoCount = reactions.filter(r => r.type === 'bravo').length

  const react = async (type) => {
    await addReaction(session.id, currentUserId, type)
  }

  const sections = (session.sections_log || [])
  const timeAgo = session.completed_at
    ? formatDistanceToNow(new Date(session.completed_at), { addSuffix: true, locale: el })
    : ''

  return (
    <div className="card fade-up" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div className="avatar avatar-sm"><Initials name={p?.full_name || p?.username} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{p?.full_name || p?.username}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{timeAgo}</div>
        </div>
        {p?.privacy_streak && p?.current_streak > 1 && (
          <div className="streak-badge">🔥 {p.current_streak}</div>
        )}
      </div>

      {p?.privacy_time && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          Έπαιξε <strong style={{ color: 'var(--text)' }}>{session.total_minutes} λεπτά</strong>
        </div>
      )}

      {p?.privacy_sections && sections.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {sections.map((s, i) => (
            <span key={i} className="tag tag-blue">{s.section_id}</span>
          ))}
        </div>
      )}

      {session.notes && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--navy-card)', borderRadius: 8, padding: '8px 10px', marginBottom: 10, lineHeight: 1.5 }}>
          "{session.notes}"
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <button onClick={() => react('fire')}
          style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `1px solid ${myFire ? 'rgba(245,166,35,0.4)' : 'var(--border)'}`, background: myFire ? 'var(--amber-dim)' : 'transparent', color: myFire ? 'var(--amber)' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font)' }}>
          🔥 {fireCount > 0 ? fireCount : ''} Φωτιά
        </button>
        <button onClick={() => react('bravo')}
          style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `1px solid ${myBravo ? 'rgba(59,125,255,0.4)' : 'var(--border)'}`, background: myBravo ? 'var(--blue-dim)' : 'transparent', color: myBravo ? 'var(--blue)' : 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font)' }}>
          👏 {bravoCount > 0 ? bravoCount : ''} Μπράβο
        </button>
        {p?.id !== currentUserId && (
          <button onClick={() => onNudgeSent(p?.id, p?.full_name || p?.username)}
            style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font)' }}>
            💬 Γράψε
          </button>
        )}
      </div>
    </div>
  )
}

export function Home() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [feed, setFeed] = useState([])
  const [friends, setFriends] = useState([])
  const [nudgeTarget, setNudgeTarget] = useState(null) // {id, name}
  const [nudgeMsg, setNudgeMsg] = useState('')

  useEffect(() => {
    if (!user) return
    getFeed(user.id).then(setFeed)
    getFriends(user.id).then(setFriends)
  }, [user])

  const today = new Date().toDateString()
  const notPlayedToday = friends.filter(f => {
    if (!f.last_session_date) return true
    return new Date(f.last_session_date).toDateString() !== today
  })

  const handleNudge = async () => {
    if (!nudgeTarget || !nudgeMsg.trim()) return
    await sendNudge(user.id, nudgeTarget.id, nudgeMsg.trim())
    setNudgeTarget(null); setNudgeMsg('')
  }

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Καλημέρα'
    if (h < 18) return 'Καλό απόγευμα'
    return 'Καλό βράδυ'
  }

  return (
    <div style={{ padding: '20px 16px' }}>
      {/* Header */}
      <div className="fade-up" style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{greeting()}, {user?.full_name?.split(' ')[0] || user?.username}</div>
        <div className="page-title" style={{ marginBottom: 4 }}>
          Πότε <span style={{ color: 'var(--blue)' }}>παίζουμε</span>;
        </div>
        {user?.current_streak > 0 && (
          <div className="streak-badge" style={{ marginTop: 8 }}>🔥 {user.current_streak} μέρες σερί</div>
        )}
      </div>

      {/* Start session CTA */}
      <button className="btn-primary fade-up fade-up-1" style={{ marginBottom: 24 }} onClick={() => navigate('/session')}>
        <span>▶</span> Ξεκίνα session τώρα
      </button>

      {/* Friends not played today */}
      {notPlayedToday.length > 0 && (
        <div className="fade-up fade-up-2" style={{ marginBottom: 20 }}>
          <div className="section-label">Δεν έπαιξαν σήμερα</div>
          {notPlayedToday.map(f => (
            <div key={f.id} className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div className="avatar avatar-sm"><Initials name={f.full_name || f.username} /></div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{f.full_name || f.username}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {f.last_session_date ? `Τελευταία: ${formatDistanceToNow(new Date(f.last_session_date), { addSuffix: true, locale: el })}` : 'Δεν έχει παίξει ακόμα'}
                  </div>
                </div>
              </div>
              <button onClick={() => setNudgeTarget({ id: f.id, name: f.full_name || f.username })}
                style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                👋 Ξύπνα
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Nudge modal */}
      {nudgeTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'flex-end' }}
          onClick={e => e.target === e.currentTarget && setNudgeTarget(null)}>
          <div style={{ background: 'var(--navy-light)', borderRadius: 'var(--radius-xl) var(--radius-xl) 0 0', padding: '24px 20px', width: '100%' }}>
            <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Ξύπνα τον/την {nudgeTarget.name}</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Στείλε ένα μήνυμα παρότρυνσης</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {['Αντε, σήμερα παίζουμε! 🎵', 'We play music! 🎹', 'Εμπρός! Έκανα ήδη session 💪'].map(m => (
                <button key={m} onClick={() => setNudgeMsg(m)}
                  style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid var(--border)', background: nudgeMsg === m ? 'var(--blue-dim)' : 'transparent', color: nudgeMsg === m ? 'var(--blue)' : 'var(--text-muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font)' }}>
                  {m}
                </button>
              ))}
            </div>
            <input className="input" placeholder="ή γράψε δικό σου μήνυμα..." value={nudgeMsg} onChange={e => setNudgeMsg(e.target.value)} style={{ marginBottom: 12 }} />
            <button className="btn-primary" onClick={handleNudge} disabled={!nudgeMsg.trim()}>Στείλε 👋</button>
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="fade-up fade-up-3">
        <div className="section-label">Η ομάδα σου — τώρα</div>
        {feed.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '32px 20px' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎵</div>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>Δεν υπάρχει ακόμα activity. Πρόσθεσε φίλους από την Κοινότητα!</div>
          </div>
        ) : feed.map(s => (
          <FeedCard key={s.id} session={s} currentUserId={user?.id} onNudgeSent={(id, name) => setNudgeTarget({ id, name })} />
        ))}
      </div>
    </div>
  )
}

export default Home
